import {
  addMonths,
  differenceInCalendarDays,
  format,
  parseISO,
  startOfMonth,
} from "date-fns";
import type {
  SeasonalEvent,
  SeasonalEventOccurrence,
} from "integrations/firebase/collections/seasonalEvents";

export const INITIAL_MONTHS_SHOWN = 3;
export const MONTHS_REVEAL_STEP = 3;

// Firestore keeps a 2-year horizon. The UI stops at this many months.
export const DISPLAY_WINDOW_MONTHS = 6;

export const displayWindowEnd = (today: string): string =>
  format(addMonths(parseISO(today), DISPLAY_WINDOW_MONTHS), "yyyy-MM-dd");

export interface CalendarEventEntry {
  event: SeasonalEvent;
  occurrence: SeasonalEventOccurrence;
  monthKey: string;
  isPast: boolean;
  isSpan: boolean;
  leadTimeDays: number;
  leadTimeWeeks: number;
}

export interface CalendarMonthGroup {
  monthKey: string;
  entries: CalendarEventEntry[];
}

/** Below this, lead time reads in days; from here on, in weeks. */
export const LEAD_TIME_DAYS_THRESHOLD = 14;

export const getLeadTimeDays = (start: string, today: string): number =>
  differenceInCalendarDays(parseISO(start), parseISO(today));

export const getLeadTimeWeeks = (days: number): number =>
  Math.max(0, Math.round(days / 7));

export type LeadTimeLabel =
  | { unit: "today" }
  | { unit: "days" | "weeks"; count: number };

// Surfaces phrase these differently, so each picks its own strings from the unit.
export const getLeadTimeLabel = (days: number): LeadTimeLabel => {
  if (days <= 0) {
    return { unit: "today" };
  }
  if (days < LEAD_TIME_DAYS_THRESHOLD) {
    return { unit: "days", count: days };
  }
  return { unit: "weeks", count: getLeadTimeWeeks(days) };
};

const toMonthKey = (date: Date): string => format(date, "yyyy-MM");

const isSpanOccurrence = (occurrence: SeasonalEventOccurrence): boolean =>
  occurrence.start !== occurrence.end;

const monthsSpannedBy = (occurrence: SeasonalEventOccurrence): string[] => {
  const start = startOfMonth(parseISO(occurrence.start));
  const end = startOfMonth(parseISO(occurrence.end));
  const keys: string[] = [];
  let cursor = start;
  while (cursor.getTime() <= end.getTime()) {
    keys.push(toMonthKey(cursor));
    cursor = addMonths(cursor, 1);
  }
  return keys;
};

export const buildCalendarMonthGroups = (
  events: SeasonalEvent[],
  today: string,
): CalendarMonthGroup[] => {
  const currentMonthKey = toMonthKey(startOfMonth(parseISO(today)));
  const windowEnd = displayWindowEnd(today);
  const windowEndMonth = windowEnd.slice(0, 7);
  const groups = new Map<string, CalendarEventEntry[]>();

  for (const event of events) {
    for (const occurrence of event.occurrences) {
      if (occurrence.start <= windowEnd) {
        const isPast = occurrence.end < today;
        const isSpan = isSpanOccurrence(occurrence);
        const leadTimeDays = getLeadTimeDays(occurrence.start, today);
        const leadTimeWeeks = getLeadTimeWeeks(leadTimeDays);

        for (const monthKey of monthsSpannedBy(occurrence)) {
          const isElapsedMonth = monthKey < currentMonthKey;
          const hidePastOutsideCurrent = isPast && monthKey !== currentMonthKey;
          if (
            !isElapsedMonth &&
            !hidePastOutsideCurrent &&
            monthKey <= windowEndMonth
          ) {
            const entries = groups.get(monthKey) ?? [];
            entries.push({
              event,
              occurrence,
              monthKey,
              isPast,
              isSpan,
              leadTimeDays,
              leadTimeWeeks,
            });
            groups.set(monthKey, entries);
          }
        }
      }
    }
  }

  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([monthKey, entries]) => ({
      monthKey,
      entries: [...entries].sort(
        (a, b) =>
          a.occurrence.start.localeCompare(b.occurrence.start) ||
          a.event.event_id.localeCompare(b.event.event_id),
      ),
    }));
};

export const formatMonthGroupLabel = (monthKey: string): string =>
  format(parseISO(`${monthKey}-01`), "MMMM yyyy");

export const formatSpanDateRange = (start: string, end: string): string =>
  `${format(parseISO(start), "MMM d")} - ${format(parseISO(end), "MMM d")}`;
